import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Store, User, Receipt, Clock } from 'lucide-react';
import ChefHeader from '../shared/ui/ChefHeader';
import { StatusBadge, LoadingView, EmptyState } from '../shared/ui';
import MapView from '../shared/MapView';
import { fetchOrder } from '../shared/services/orderService';
import { formatCurrency, formatDateTime, toDate, toLatLng } from '../shared/models';
import { displayOrderNumber } from '../components/earnings/useDeliveredOrders';
import useRoadRoute from '../shared/useRoadRoute';
import { History } from 'lucide-react';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-2">
      <h3 className="font-bold text-gray-900">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value, bold = false, valueCls = 'text-gray-800' }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-4">
      <span className={`text-sm ${bold ? 'font-bold text-gray-900' : 'text-gray-500'}`}>{label}</span>
      <span className={`text-sm text-right ${bold ? 'font-bold' : 'font-medium'} ${valueCls}`}>{value}</span>
    </div>
  );
}

// Mirrors iOS DriverDeliveryDetailView (order info, customer, restaurant,
// items, earnings) plus the payout breakdown, timestamps, and map pins.
export default function DeliveryHistoryDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchOrder(orderId)
      .then(o => { if (alive) { setOrder(o); setLoading(false); } })
      .catch(e => { console.error('fetchOrder', e); if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orderId]);

  const markers = useMemo(() => {
    if (!order) return [];
    const d = order.raw || {};
    const rest = toLatLng(order.restaurantLocation?.coordinates)
      || toLatLng(order.restaurantLocation)
      || toLatLng(d.pickupLocation?.coordinates || d.pickupLocation);
    const cust = toLatLng(order.deliveryAddress?.coordinates)
      || toLatLng(d.deliveryLocation?.coordinates || d.deliveryLocation);
    const list = [];
    if (rest) list.push({ ...rest, kind: 'restaurant', popup: order.restaurantName || 'Restaurant' });
    if (cust) list.push({ ...cust, kind: 'customer', popup: order.customerName || 'Customer' });
    return list;
  }, [order]);

  // Road-following routed line between pickup and drop-off.
  const route = useRoadRoute(markers[0], markers[1]);

  if (loading) {
    return (
      <div>
        <ChefHeader title="Order Details" />
        <LoadingView label="Loading order…" />
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <ChefHeader title="Order Details" />
        <EmptyState icon={History} title="Order not found" subtitle="This delivery could not be loaded." />
      </div>
    );
  }

  const d = order.raw || {};
  const fee = Number(order.deliveryFee) || 0;
  const tip = Number(order.tipAmount) || 0;
  const commission = Number.isFinite(Number(order.driverCommission))
    ? Number(order.driverCommission) : fee * 0.9;
  const payout = Number.isFinite(Number(order.driverPayout))
    ? Number(order.driverPayout) : commission + tip;

  const acceptedAt = toDate(d.acceptedAt) || order.createdAt;
  const pickedUpAt = order.pickupTime || toDate(d.actualPickupTime);
  const deliveredAt = toDate(d.actualDeliveryTime) || order.updatedAt;
  const addressText = [order.deliveryAddress?.street, order.deliveryAddress?.city]
    .filter(Boolean).join(', ') || order.customerAddress;

  return (
    <div>
      <ChefHeader
        title="Order Details"
        right={
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-2xl font-extrabold text-gray-900">
              Order #{displayOrderNumber(order)}
            </h2>
            <StatusBadge status={order.status} />
          </div>
          <p className="text-sm text-gray-400">
            Completed on {formatDateTime(deliveredAt || order.createdAt)}
          </p>
        </div>

        {/* Map */}
        {markers.length > 0 && (
          <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-52">
            <MapView markers={markers} route={route} />
          </div>
        )}

        {/* Customer */}
        <Section title="Customer Details">
          <div className="flex items-center gap-2 text-sm text-gray-800">
            <User size={15} className="text-gray-400" />
            <span>{order.customerName || 'Customer'}</span>
          </div>
          {order.customerPhone ? (
            <a href={`tel:${order.customerPhone}`} className="flex items-center gap-2 text-sm text-blue-500">
              <Phone size={15} />
              <span>{order.customerPhone}</span>
            </a>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Phone size={15} />
              <span>Contact Available</span>
            </div>
          )}
          {addressText && <p className="text-sm text-gray-500">Address: {addressText}</p>}
        </Section>

        {/* Restaurant */}
        <Section title="Restaurant Details">
          <div className="flex items-center gap-2 text-sm text-gray-800">
            <Store size={15} className="text-gray-400" />
            <span>{order.restaurantName || 'Restaurant'}</span>
          </div>
        </Section>

        {/* Items */}
        <Section title={`Order Items${order.orderItems.length ? ` (${order.orderItems.length})` : ''}`}>
          {order.orderItems.length === 0 ? (
            <p className="text-sm text-gray-400">No items recorded</p>
          ) : (
            order.orderItems.map((it, i) => (
              <div key={it.id || i} className="flex justify-between gap-3 text-sm">
                <span className="text-gray-800">
                  {it.quantity}x {it.menuItemName || it.name}
                </span>
                <span className="text-gray-800 font-medium">{formatCurrency(it.totalPrice)}</span>
              </div>
            ))
          )}
          <div className="border-t border-gray-100 pt-2 space-y-1">
            <Row label="Subtotal" value={formatCurrency(order.subtotal)} />
            <Row label="Order Total" value={formatCurrency(order.totalAmount)} bold />
          </div>
        </Section>

        {/* Earnings breakdown */}
        <Section title="Your Earnings">
          <div className="flex items-center gap-2 text-gray-400">
            <Receipt size={15} />
            <span className="text-xs">Payout breakdown</span>
          </div>
          <Row label="Customer Tip" value={formatCurrency(tip)} />
          <div className="border-t border-gray-100 pt-2">
            <div className="flex justify-between">
              <span className="font-bold text-gray-900">Driver Payout</span>
              <span className="font-bold text-green-600">{formatCurrency(payout)}</span>
            </div>
          </div>
        </Section>

        {/* Timestamps */}
        <Section title="Timeline">
          <div className="flex items-center gap-2 text-gray-400">
            <Clock size={15} />
            <span className="text-xs">Delivery timestamps</span>
          </div>
          <Row label="Accepted" value={acceptedAt ? formatDateTime(acceptedAt) : null} />
          <Row label="Picked Up" value={pickedUpAt ? formatDateTime(pickedUpAt) : null} />
          <Row label="Delivered" value={deliveredAt ? formatDateTime(deliveredAt) : null} />
          <Row label="Created" value={order.createdAt ? formatDateTime(order.createdAt) : null} />
        </Section>
      </div>
    </div>
  );
}
